import { useEffect, useState } from 'react';
import { evalApi, type EvalSummary, type RecognitionEvalResult } from '../../lib/evalApi';

function pct(correct: number, total: number): string {
  if (total === 0) return '—';
  return `${Math.round((correct / total) * 100)}%`;
}

export function EvalPage() {
  const [summary, setSummary] = useState<EvalSummary | null>(null);
  const [recognition, setRecognition] = useState<RecognitionEvalResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    evalApi.summary().then(setSummary).catch((e) => setError((e as Error).message));
  }, []);

  const runRecognition = async () => {
    setRunning(true);
    setError(null);
    try {
      setRecognition(await evalApi.recognition());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="flex-col" style={{ gap: 14 }}>
      <h1 style={{ margin: 0 }}>Eval dashboard</h1>
      <p className="muted small" style={{ marginTop: 0 }}>
        Turns "the AI feels generic" into numbers — with no model training. Recognition scoring checks whether the
        catalogue actually resolves the vocabulary it claims to know; the corrections breakdown shows where the AI
        was wrong and which layer to fix. Edit an invariant/alias, re-run, and watch the score move.
      </p>

      {error && <div className="card" style={{ borderColor: 'var(--bad)', color: 'var(--bad)' }}>{error}</div>}

      {summary && (
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Catalogue</h2>
          <div className="row" style={{ gap: 24, flexWrap: 'wrap' }}>
            <div><div className="muted small">Concepts</div><div style={{ fontSize: 22 }}>{summary.conceptCount}</div></div>
            <div><div className="muted small">Taggable</div><div style={{ fontSize: 22 }}>{summary.taggableCount}</div></div>
          </div>
          <h3>Corrections by failure category</h3>
          <div className="row" style={{ gap: 16, flexWrap: 'wrap' }}>
            {['recognition', 'observation', 'judgment', 'data', 'uncategorised'].map((k) => (
              <div key={k}>
                <div className="muted small">{k}</div>
                <div style={{ fontSize: 20 }}>{summary.corrections[k] ?? 0}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>Recognition eval</h2>
          <span className="spacer" />
          <button type="button" className="btn small btn-primary" onClick={() => void runRecognition()} disabled={running}>
            {running ? 'Running…' : 'Run recognition eval'}
          </button>
        </div>
        <p className="muted small">
          Exact: every alias must resolve back to its own concept (catches collisions). Semantic: each concept's
          definition, run through the in-memory cosine recogniser, should rank itself first (needs the embeddings
          API).
        </p>
        {recognition && (
          <div className="flex-col" style={{ gap: 10 }}>
            <div className="row" style={{ gap: 24, flexWrap: 'wrap' }}>
              <div>
                <div className="muted small">Exact alias coverage</div>
                <div style={{ fontSize: 22 }}>
                  {pct(recognition.exact.correct, recognition.exact.total)}{' '}
                  <span className="muted small">({recognition.exact.correct}/{recognition.exact.total})</span>
                </div>
              </div>
              <div>
                <div className="muted small">Semantic self-recognition</div>
                <div style={{ fontSize: 22 }}>
                  {recognition.semantic.ran
                    ? `${pct(recognition.semantic.correct, recognition.semantic.total)} (${recognition.semantic.correct}/${recognition.semantic.total})`
                    : '—'}
                </div>
              </div>
            </div>
            {recognition.exact.collisions.length > 0 && (
              <div>
                <strong className="small">Alias collisions</strong>
                {recognition.exact.collisions.map((c, i) => (
                  <div key={i} className="muted small mono">
                    "{c.alias}" expected {c.expected} → got {c.got}
                  </div>
                ))}
              </div>
            )}
            {recognition.semantic.note && (
              <div className="muted small">{recognition.semantic.note}</div>
            )}
            {recognition.semantic.misses.length > 0 && (
              <div>
                <strong className="small">Semantic misses (nearest neighbour wasn't itself)</strong>
                {recognition.semantic.misses.slice(0, 20).map((m, i) => (
                  <div key={i} className="muted small mono">
                    {m.concept} → {m.got ?? '(none)'}{m.score != null ? ` @ ${m.score}` : ''}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
